import { Link } from 'react-router-dom';
import { ApiQuickstart } from './ApiQuickstart.js';

/**
 * The homepage hero.
 *
 * Left-aligned on the Cyble navy panel, matching the brand voice used across the product: no em
 * dashes, no hype adjectives, no competitor named. The primary action scrolls to the submit tool
 * further down the page rather than navigating away, because the tool is the product.
 *
 * The API card sits INSIDE the hero on purpose: a developer evaluating the platform should see a
 * runnable command without scrolling, and every command on it works with no account. It is placed
 * after the calls to action so the human path (analyze a file) still reads first.
 */
export function HomeHero({ cybleUpgrading }: { cybleUpgrading?: boolean }) {
  return (
    <section className="overflow-hidden rounded-md bg-brand-navy px-5 py-8 sm:px-8 sm:py-12">
      <p className="text-micro font-semibold uppercase tracking-[0.2em] text-accent-500">
        Malware analysis, powered by Cyble
      </p>
      {/* One large size, then nothing until body. The old hero was 36px, which is a heading rather
          than a statement, and it sat in the middle of a five-step ladder that flattened the whole
          page. `text-hero` carries its own tight tracking from the token scale. */}
      <h1 className="mt-3 max-w-3xl text-3xl font-bold text-white sm:text-hero">
        Analyze any file or URL. Get a verdict you can audit.
      </h1>
      <p className="mt-5 max-w-2xl text-sm leading-relaxed text-white/70 sm:text-body">
        Submit a file, a URL, or a hash. MalwareAnalyzer runs multi-engine static scanning, detonates
        executables in full-system sandboxes with controlled network egress, captures the behavior,
        extracts the indicators, and shows every signal behind the score.
        {cybleUpgrading ? ' Cyble Vision intel returns after its upgrade.' : ''}
      </p>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <a
          href="#submit"
          className="rounded-md bg-cta px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cta-hover"
        >
          Analyze a file or URL
        </a>
        <Link
          to="/community"
          className="rounded-md border border-white/25 px-4 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-white/5"
        >
          Browse recent threats
        </Link>
        <span className="text-micro text-white/45">Free and unlimited for public analysis</span>
      </div>
      <div className="mt-6 max-w-3xl">
        <p className="mb-2 text-micro font-semibold uppercase tracking-[0.2em] text-white/50">
          Use it from your terminal
        </p>
        <ApiQuickstart tone="dark" />
      </div>
    </section>
  );
}
